import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { BLOG_POSTS } from '@/data/content';
import { Clock, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!post) return <Navigate to="/resources/blog" replace />;

  const idx = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const next = BLOG_POSTS[(idx + 1) % BLOG_POSTS.length];

  return (
    <div>
      <article>
        <div className="max-w-3xl mx-auto px-6 pt-16 pb-8">
          <Link to="/resources/blog" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-300">
            <ArrowLeft className="w-3.5 h-3.5" /> All articles
          </Link>
          <div className="flex items-center gap-3 mt-6 text-[10px] font-mono uppercase tracking-widest">
            <span className="text-cyan-300">{post.category}</span>
            <span className="text-slate-600">•</span>
            <span className="text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-bold leading-[1.1] mt-3">
            {post.title}
          </h1>
          <p className="text-slate-400 mt-5 text-base md:text-lg leading-relaxed">
            {post.summary}
          </p>
        </div>

        <div className={post.narrowImage ? "max-w-4xl mx-auto px-6" : "max-w-5xl mx-auto px-6"}>
          <div className="relative aspect-[21/9] rounded-2xl overflow-hidden border border-white/10">
            <img
              src={post.image}
              alt={post.title}
              onError={(e) => { e.currentTarget.src = post.fallback; }}
              className={`absolute inset-0 w-full h-full ${post.uncroppedImage ? "object-cover object-top" : "object-cover"}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07090c]/40 to-transparent" />
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-14 space-y-10">
          {post.sections.map((s, i) => (
            <section key={i}>
              {!s.hideSectionLabel && <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-300">Section 0{i + 1}</div>}
              <h2 className="font-display text-2xl md:text-3xl font-bold mt-2">{s.h}</h2>
              <p className="text-slate-300 mt-3 leading-relaxed text-base">{s.p}</p>
            </section>
          ))}

          <div className="pt-6 border-t border-white/10 flex items-center justify-between gap-4">
            <Link to="/resources/blog" className="text-sm text-slate-400 hover:text-cyan-300 inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> All articles
            </Link>
            <Link to={`/resources/blog/${next.slug}`} className="text-sm text-cyan-300 inline-flex items-center gap-1">
              Next: {next.title.split(':')[0]} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </article>

      <section className="border-t border-white/5 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-display text-2xl md:text-3xl font-bold">See it running on your feeds.</h3>
          <p className="text-slate-400 mt-3">Bring a camera feed. We'll read it live.</p>
          <Link to="/contact" className="inline-block mt-6">
            <Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-900">Book a demo <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
